/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { observer } from 'mobx-react-lite'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Select, Input as AntInput } from 'antd'
import req from '@sportninja/common/api/request'
import { useMobxStore } from '../../../state'
import { EventCard } from './ConfirmProfile'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import colors from '@sportninja/common/constants/appColors'
import { EventDetails } from './Login'
import Icon from '../../../components/Icon'
import { useI18n } from '@shopify/react-i18n'
import { NewModal } from 'src/components/NewModal'
import { ModalHeader } from 'src/pages/GeneralSettings/UpgradeModal'
import { Input } from './NewPlayerRegistration'
import { Button } from 'src/components/Button'
import BannerController from 'src/components/Banner/BannerController'
import Banner from 'src/components/Banner'
import PhoneInput from 'src/components/PhoneInput'
import { RequestsService } from 'src/pages/Onboarding/SignUp/requests'

const DropdownIndicator = ({ onClick, ...props }) => {
  return (
    <div
      onClick={onClick}
      css={css`
        width: 30px;
        height: 24px;
        display: flex;
        align-items: center;
        justify-content: center;
      `}
      {...props.innerProps}
    >
      <Icon
        name='chevron-down'
        color='#898a8a'
        hoverColor={colors.SOFT_STEEL}
      />
    </div>
  )
}

const TeamControl = ({ onClick, ...props }) => {
  const value = props.hasValue
    ? props.getValue()[0]
    : { name: 'Select...', image: null }

  return (
    <div
      onClick={onClick}
      css={css`
        flex: 1;
        display: flex;
        align-items: center;
        padding: 8px;
      `}
    >
      {value?.image?.full_path && (
        <img
          src={value?.image?.full_path}
          css={css`
            width: 24px;
            height: 24px;
            margin-right: 8px;
          `}
        />
      )}
      <span
        css={css`
          font-size: 16px;
          opacity: ${value.name === 'Select...' ? 0.5 : 1};
        `}
      >
        {value.name}
      </span>
    </div>
  )
}

const TeamOptions = ({ onClick, innerRef, innerProps, data, isDarkMode }) => {
  return (
    <div
      ref={innerRef}
      {...innerProps}
      onClick={() => {
        onClick()
        innerProps.onClick()
      }}
      css={css`
        display: flex;
        align-items: center;
        padding: 12px 8px;
        cursor: pointer;
        background-color: transparent;
        transition: all 0.2s ease-in-out;

        &:hover {
          background-color: ${isDarkMode ? '#12161C' : 'white'};
        }
      `}
    >
      {data?.image?.full_path && (
        <img
          src={data?.image?.full_path}
          css={css`
            width: 24px;
            height: 24px;
          `}
        />
      )}
      <span
        css={css`
          font-size: 16px;
          margin-left: 8px;
        `}
      >
        {data.name}
      </span>
    </div>
  )
}

const DivisionControl = ({ onClick, ...props }) => {
  const value = props.hasValue
    ? props.getValue()[0]
    : { name: props.isDisabled ? 'Select a team first' : 'Select...' }

  return (
    <div
      onClick={onClick}
      css={css`
        align-items: center;
        padding: 8px;
        flex: 1;
        display: flex;
        cursor: ${props.isDisabled ? 'not-allowed' : 'pointer'};
      `}
    >
      <span
        css={css`
          font-size: 16px;
          opacity: ${props.isDisabled ? 0.5 : 1};
          cursor: ${props.isDisabled ? 'not-allowed' : 'pointer'};
        `}
      >
        {value.name}
      </span>
    </div>
  )
}

const DivisionOptions = ({
  onClick,
  innerRef,
  innerProps,
  data,
  isDarkMode,
}) => {
  return (
    <div
      ref={innerRef}
      {...innerProps}
      onClick={() => {
        onClick()
        innerProps.onClick()
      }}
      css={css`
        padding: 12px 8px;
        cursor: pointer;
        background-color: transparent;
        transition: all 0.2s ease-in-out;
        display: flex;
        flex: 1;

        &:hover {
          background-color: ${isDarkMode ? '#12161C' : 'white'};
        }
      `}
    >
      <span
        css={css`
          font-size: 16px;
          margin-left: 8px;
        `}
      >
        {data.name}
      </span>
    </div>
  )
}

const Row = ({ label, value, isDarkMode }) => (
  <div
    css={css`
      width: 100%;
      margin: 16px 0;

      @media (max-width: 768px) {
        margin: 0;
        margin-top: 6px;
      }
    `}
  >
    <span
      css={css`
        font-size: 14px;
      `}
    >
      {label}
    </span>
    <div
      css={css`
        display: flex;
        flex-direction: row;
        justify-content: space-between;
        align-items: center;
        padding: 8px;
        flex: 1;
        height: 40px;
        background-color: ${isDarkMode ? '#33363B' : '#f5f5f5'};
        border: 1px solid ${colors.ATTENDANCE_GRAY};
        border-radius: 4px;
        margin-top: 8px;
      `}
    >
      <span
        css={css`
          font-size: 16px;
        `}
      >
        {value}
      </span>
    </div>
  </div>
)

export const NewTeamRegistration = observer(({ isDarkMode }) => {
  const {
    registrationTypes: {
      state: { age_range_type, gender_type },
    },
    registrationInvite: {
      state: { eventInfo, userExtraInfo, userTeams, newTeamId, sports },
      confirmTeamAndDivisionSelect,
      pushToStep,
    },
  } = useMobxStore()

  const [i18n] = useI18n()

  const [divisionInfo, setDivisionInfo] = useState({
    registrationId: null,
    divisionId: null,
    costPerTeam: '-',
    deposit: '-',
    age: '-',
    gender: '-',
    name: '-',
    depositRequired: '-',
    currency: {
      name: 'CAD',
    },
    singlePayment: false,
  })

  const [teamMenuIsOpen, setTeamMenuIsOpen] = useState(false)
  const [divisionMenuIsOpen, setDivisionMenuIsOpen] = useState(false)
  const [teamOfficialMenuIsOpen, setTeamOfficialMenuIsOpen] = useState(false)
  const [newTeamOfficialMenuIsOpen, setNewTeamOfficialMenuIsOpen] =
    useState(false)
  const [disabled, setDisabled] = useState(true)
  const [isTeamSelected, setIsTeamSelected] = useState(false)
  const [currentTeamOfficialPhone, setCurrentTeamOfficialPhone] = useState('')
  const [newTeamOfficialInfo, setNewTeamOfficialInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    type: '',
    title: '',
  })
  const [couponCode, setCouponCode] = useState('')
  const [loadingCoupon, setLoadingCoupon] = useState(false)
  const [couponError, setCouponError] = useState('')
  const [couponInfo, setCouponInfo] = useState({
    discount: 0,
    type: '',
    id: '',
  })

  const [newTeamOfficialErrorMessage, setNewTeamOfficialErrorMessage] =
    useState('')
  const [selectedCountry, setSelectedCountry] = useState('')
  const [zipCode, setZipCode] = useState('')

  const newTeamOfficialModalRef = useRef(null)

  const newTeam = useMemo(() => {
    return userTeams?.find(
      (team) => team?.id === newTeamId || team?.uid === newTeamId
    )
  }, [newTeamId])

  const teamOfficialTypes = useMemo(() => {
    const sportId = eventInfo?.sport_id
    const selectedSport = sports?.find((sport) => sport.sport_id === sportId)

    const teamOfficialTypes = selectedSport?.data?.find(
      (el) => el.team_official_types
    )?.team_official_types

    const types = [
      ...teamOfficialTypes.map((el) => {
        return {
          label: el.name,
          value: el.id,
        }
      }),
    ]

    return types
  }, [sports, eventInfo?.sport_id])

  const [selectedTeam, setSelectedTeam] = useState(newTeam)
  const [teamOfficialInformation, setTeamOfficialInformation] = useState([
    {
      firstName: userExtraInfo?.name_first,
      lastName: userExtraInfo?.name_last,
      email: userExtraInfo?.email,
      phone: '',
      type: '1',
      title: 'Manager',
      isTreasurer: true,
    },
  ])
  const [userSelectedHisTitle, setUserSelectedHisTitle] = useState(false)

  const countries = useMemo(() => {
    const countryList = RequestsService.getCountries()

    const us = countryList.find((el) => el.iso_3166_2 === 'US')
    const cad = countryList.find((el) => el.iso_3166_2 === 'CA')

    if (us && cad) {
      countryList.unshift(us, cad)
    }

    return countryList
  }, [])

  const filterOption = (input, option) =>
    (option?.label ?? '').toLowerCase().includes(input.toLowerCase())

  const validateCouponCode = useCallback(async () => {
    if (couponCode === '') {
      return
    }

    setLoadingCoupon(true)
    setDisabled(true)

    try {
      const res = await req(`/registration/${eventInfo?.id}/coupon/verify`, {
        method: 'POST',
        body: JSON.stringify({
          code: couponCode,
        }),
      })

      setCouponInfo({
        discount: res?.data?.discount,
        type: res?.data?.discount_type,
        id: res?.data?.id,
      })
    } catch (e) {
      console.error('CPN CD: ', e?.message)
      return BannerController.add(({ ...props }) => (
        <Banner
          duration={undefined}
          onClose={undefined}
          onClick={undefined}
          subChildren={undefined}
          {...props}
          type='failed'
        >
          {e?.message}
        </Banner>
      ))
    } finally {
      setLoadingCoupon(false)
      if (divisionInfo.registrationId) {
        setDisabled(false)
      }
    }
  }, [couponCode, loadingCoupon, eventInfo?.id, divisionInfo])

  useEffect(() => {
    if (
      selectedTeam &&
      divisionInfo?.registrationId &&
      selectedCountry &&
      zipCode &&
      zipCode.length > 2 &&
      userSelectedHisTitle
    ) {
      setDisabled(false)
    } else {
      setDisabled(true)
    }
  }, [
    divisionInfo,
    selectedTeam,
    selectedCountry,
    zipCode,
    userSelectedHisTitle,
  ])

  return (
    <div
      css={css`
        color: ${isDarkMode ? colors.WHITE : 'black'};

        .ant-select-selector {
          background-color: ${isDarkMode ? '#33363B' : '#f5f5f5'} !important;
          border: 1px solid ${colors.ATTENDANCE_GRAY} !important;
        }
      `}
    >
      <button
        onClick={() => pushToStep(0)}
        css={css`
          padding-top: 16px;
          align-items: center;
          display: flex;
          gap: 16px;
        `}
      >
        <Icon name='chevron-left' color={colors.PALE_SKY} />
        <p
          css={css`
            color: ${colors.PALE_SKY};
            font-size: 16px;
            font-style: normal;
            font-weight: 500;
            line-height: normal;
          `}
        >
          Back
        </p>
      </button>
      <h1
        css={css`
          color: ${isDarkMode ? colors.WHITE : colors.HEADER};
          text-align: center;
          margin-top: 32px;
          font-size: 34px;
          font-weight: 700;
          font-family: ${isCanlan
            ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
            : 'Rift, Arial, Helvetica, sans-serif'};
        `}
      >
        NEW REGISTRATION
      </h1>
      <EventCard eventInfo={eventInfo} isDarkMode={isDarkMode} />
      <p
        css={css`
          font-size: 18px;
          font-style: normal;
          font-weight: 700;
          line-height: normal;
          letter-spacing: 0.9px;
          margin: 32px 0 16px 0;
          color: ${isDarkMode ? colors.WHITE : colors.HEADER};
        `}
      >
        TEAM INFO
      </p>

      <p
        css={css`
          font-size: 14px;
          margin-bottom: 8px;
        `}
      >
        My Teams
      </p>
      <Select
        css={css`
          width: 100%;
        `}
        placeholder='Select...'
        defaultValue={selectedTeam?.uid || selectedTeam?.id}
        onChange={(e) => {
          const theTeam = userTeams.find(
            (team) => team.uid === e || team.id === e
          )
          setSelectedTeam({ ...theTeam })
          setIsTeamSelected(true)
        }}
        options={
          userTeams
            ? userTeams.map((el) => {
                return {
                  label: el.name,
                  value: el.uid || el.id,
                }
              })
            : []
        }
      />
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          margin-top: 10px;
        `}
      >
        <button
          onClick={() => pushToStep(5)}
          css={css`
            color: ${colors.DEFAULT_FLAIR};
            font-weight: 700;
            transition: all 0.2s ease-in-out;

            &:hover {
              color: ${colors.DEFAULT_FLAIR_HOVER};
            }
          `}
        >
          Create New Team
        </button>
      </div>
      <div
        css={css`
          margin: 32px 0;
          height: 1px;
          background-color: #dfdfe0;
        `}
      />
      <p
        css={css`
          font-size: 18px;
          font-style: normal;
          font-weight: 700;
          line-height: normal;
          letter-spacing: 0.9px;
          margin: 32px 0 6px 0;
          color: ${isDarkMode ? colors.WHITE : colors.HEADER};
        `}
      >
        TEAM OFFICIAL INFO
      </p>
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;

          @media (max-width: 768px) {
            flex-direction: column;
          }
        `}
      >
        <Row
          label='First Name'
          value={userExtraInfo?.name_first}
          isDarkMode={isDarkMode}
        />
        <Row
          label='Last Name'
          value={userExtraInfo?.name_last}
          isDarkMode={isDarkMode}
        />
      </div>
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 8px;

          @media (max-width: 768px) {
            flex-direction: column;

            > div {
              width: 100%;
            }
          }
        `}
      >
        <div
          css={css`
            flex: 1;
          `}
        >
          <Row
            label='Email Address'
            value={userExtraInfo?.email}
            isDarkMode={isDarkMode}
          />
        </div>
        <div
          css={css`
            flex: 1;
          `}
        >
          <p>Title *</p>
          <div
            css={css`
              margin-top: 8px;
            `}
          >
            <Select
              css={css`
                width: 100%;
              `}
              onChange={(e) => {
                const selectedItem = teamOfficialInformation.find(
                  (item) => item.email === userExtraInfo?.email
                )
                const officialInfo = teamOfficialTypes.find(
                  (el) => el.value === e
                )

                setTeamOfficialInformation((prev) => {
                  const newTeamOfficialInformation = [...prev]
                  newTeamOfficialInformation.splice(
                    newTeamOfficialInformation.indexOf(selectedItem),
                    1,
                    {
                      ...selectedItem,
                      type: officialInfo.value,
                      title: officialInfo.label,
                    }
                  )
                  return newTeamOfficialInformation
                })
                setUserSelectedHisTitle(true)
              }}
              placeholder='Select...'
              options={teamOfficialTypes ?? []}
            />
          </div>
        </div>
        <div
          css={css`
            display: flex;
            align-items: flex-start;
            gap: 8px;
            flex-direction: column;
            flex: 1;

            .ant-input {
              background-color: ${isDarkMode
                ? '#33363B'
                : '#f5f5f5'} !important;
              border: 1px solid ${colors.ATTENDANCE_GRAY} !important;
            }
          `}
        >
          <p>Phone Number</p>
          <PhoneInput
            value={currentTeamOfficialPhone || ''}
            onChange={(e) => {
              if (e) {
                setCurrentTeamOfficialPhone(e)
              }
            }}
            onBlur={() => {
              const selectedItem = teamOfficialInformation.find(
                (item) => item.email === userExtraInfo?.email
              )
              setTeamOfficialInformation((prev) => {
                const newTeamOfficialInformation = [...prev]
                newTeamOfficialInformation.splice(
                  newTeamOfficialInformation.indexOf(selectedItem),
                  1,
                  {
                    ...selectedItem,
                    phone: currentTeamOfficialPhone,
                  }
                )
                return newTeamOfficialInformation
              })
            }}
          />
        </div>
      </div>
      {teamOfficialInformation.length > 1 && (
        <p
          css={css`
            font-size: 18px;
            font-style: normal;
            font-weight: 700;
            line-height: normal;
            letter-spacing: 0.9px;
            margin: 32px 0 8px 0;
            color: ${isDarkMode ? colors.WHITE : colors.HEADER};
            text-transform: uppercase;
          `}
        >
          Additional Team Officials
        </p>
      )}

      {teamOfficialInformation.length > 1 && (
        <div
          css={css`
            border: 1px solid ${colors.ATTENDANCE_GRAY};
            border-radius: 4px;
            overflow: hidden;
          `}
        >
          {teamOfficialInformation.map((el, index) => {
            if (index === 0) {
              return null
            }

            return (
              <div
                key={el.email}
                css={css`
                  display: flex;
                  justify-content: space-between;
                  background-color: ${index % 2 === 0
                    ? isDarkMode
                      ? '#33363B'
                      : '#f5f5f5'
                    : 'transparent'};
                  padding: 12px 8px;
                `}
              >
                <p
                  css={css`
                    font-size: 16px;
                    font-style: normal;
                    font-weight: 500;
                    line-height: normal;
                    color: ${isDarkMode ? colors.WHITE : colors.HEADER};
                  `}
                >
                  {el.firstName} - {el.title}
                </p>
                <button
                  onClick={() => {
                    const newTeamOfficialInformation = [
                      ...teamOfficialInformation,
                    ]
                    newTeamOfficialInformation.splice(index, 1)
                    setTeamOfficialInformation(newTeamOfficialInformation)
                  }}
                >
                  <Icon name='trash' color={colors.RED} />
                </button>
              </div>
            )
          })}
        </div>
      )}

      <div
        css={css`
          display: flex;
          justify-content: flex-end;
          margin-top: 14px;
        `}
      >
        <button
          onClick={() => newTeamOfficialModalRef?.current?.openModal()}
          css={css`
            color: ${colors.DEFAULT_FLAIR};
            font-weight: 700;
            transition: all 0.2s ease-in-out;

            &:hover {
              color: ${colors.DEFAULT_FLAIR_HOVER};
            }
          `}
        >
          Create New Team Official
        </button>
      </div>
      <div>
        <div
          css={css`
            margin: 32px 0;
            height: 1px;
            background-color: #dfdfe0;
          `}
        />

        <p
          css={css`
            font-size: 18px;
            font-style: normal;
            font-weight: 700;
            line-height: normal;
            letter-spacing: 0.9px;
            color: ${isDarkMode ? colors.WHITE : colors.HEADER};
            margin: 32px 0 16px 0;
          `}
        >
          ADDRESS INFO
        </p>
        <div
          css={css`
            display: flex;
            gap: 16px;

            @media (max-width: 768px) {
              flex-direction: column;
            }
          `}
        >
          <div
            css={css`
              flex: 1;
            `}
          >
            <p
              css={css`
                font-size: 14px;
                margin-bottom: 8px;
              `}
            >
              Country *
            </p>
            <Select
              css={css`
                width: 100%;
              `}
              showSearch
              filterOption={filterOption}
              placeholder='Select...'
              onChange={(e) => {
                setSelectedCountry(e)
              }}
              options={
                countries
                  ? countries.map((el) => {
                      return {
                        label: el?.name,
                        value: el?.iso_3166_2,
                      }
                    })
                  : []
              }
            />
          </div>
          <div
            css={css`
              flex: 1;
              .ant-input {
                background-color: ${isDarkMode
                  ? '#33363B'
                  : '#f5f5f5'} !important;
                border: 1px solid ${colors.ATTENDANCE_GRAY} !important;
              }
            `}
          >
            <p
              css={css`
                font-size: 14px;
                margin-bottom: 8px;
              `}
            >
              Zip/Postal *
            </p>
            <AntInput
              placeholder='Enter Zip/Postal Code'
              value={zipCode}
              onChange={(e) => {
                setZipCode(e.target.value)
              }}
            />
          </div>
        </div>
      </div>

      <div
        css={css`
          margin: 32px 0;
          height: 1px;
          background-color: #dfdfe0;
        `}
      />

      <p
        css={css`
          font-size: 18px;
          font-style: normal;
          font-weight: 700;
          line-height: normal;
          letter-spacing: 0.9px;
          color: ${isDarkMode ? colors.WHITE : colors.HEADER};
          margin: 32px 0 20px 0;
        `}
      >
        REGISTRATION INFO
      </p>
      <div
        css={css`
          display: flex;
          align-items: center;
          gap: 16px;

          @media (max-width: 768px) {
            flex-direction: column;

            > div {
              width: 100%;
            }
          }
        `}
      >
        <div
          css={css`
            flex: 1;
          `}
        >
          <p
            css={css`
              font-size: 14px;
              margin-bottom: 8px;
            `}
          >
            Division
          </p>
          <Select
            css={css`
              width: 100%;
            `}
            placeholder='Select...'
            onChange={(e) => {
              const theDivision = eventInfo?.divisions.find(
                (division) => division.uid === e || division.id === e
              )
              setDivisionInfo({
                registrationId: theDivision.id,
                divisionId: theDivision.schedule_id,
                costPerTeam: theDivision.team_cost,
                deposit: theDivision.deposit,
                age: theDivision.division_age?.id,
                gender: theDivision.division_gender?.id,
                name: theDivision.name,
                currency: theDivision.currency,
                depositRequired: theDivision.deposit_required,
                singlePayment: theDivision.single_payment,
              })
            }}
            options={
              eventInfo?.divisions
                ? eventInfo?.divisions.map((el) => {
                    return {
                      label: el.name,
                      value: el.uid || el.id,
                    }
                  })
                : []
            }
          />
        </div>
        <div
          css={css`
            flex: 1;
            display: flex;
            gap: 8px;
            align-items: center;
          `}
        >
          <div
            css={css`
              flex: 10;
            `}
          >
            <div
              css={css`
                .ant-input {
                  background-color: ${isDarkMode
                    ? '#33363B'
                    : '#f5f5f5'} !important;
                  border: 1px solid ${colors.ATTENDANCE_GRAY} !important;
                }
              `}
            >
              <p
                css={css`
                  font-size: 14px;
                  margin-bottom: 8px;
                `}
              >
                Coupon Code
              </p>
              <AntInput
                value={couponCode}
                placeholder='Enter Coupon Code'
                onChange={(e) => {
                  setCouponCode(e.target.value)
                }}
              />
            </div>
          </div>
          <div
            css={css`
              flex: 1;
              margin-top: 20px;
            `}
          >
            <Button
              icon={
                loadingCoupon ? (
                  <Icon name='spinner' color={colors.WHITE} />
                ) : (
                  <Icon name='check' color={colors.WHITE} />
                )
              }
              onClick={validateCouponCode}
              disabled={loadingCoupon}
            />
          </div>
        </div>
      </div>
      <div
        css={css`
          margin-top: 22px;
        `}
      >
        {divisionInfo.deposit == '-' && (
          <>
            <p>Select a division to get more info *</p>
          </>
        )}
      </div>
      <div
        css={css`
          background-color: ${isDarkMode ? '#33363B' : '#f5f5f5'};
          padding: 16px;
          border-radius: 8px;
          margin: 12px 0 32px 0;
        `}
      >
        {!divisionInfo.singlePayment && (
          <EventDetails
            label='Deposit'
            value={
              divisionInfo.deposit !== '-'
                ? i18n.formatCurrency(divisionInfo?.deposit, {
                    currency: divisionInfo?.currency?.name,
                    form: 'explicit',
                  })
                : '-'
            }
          />
        )}
        <EventDetails
          label={
            divisionInfo.singlePayment ? 'Registration Cost' : 'Cost Per Team'
          }
          value={
            divisionInfo.costPerTeam !== '-'
              ? i18n.formatCurrency(
                  divisionInfo?.costPerTeam -
                    (couponInfo.discount
                      ? couponInfo.type === 'amount'
                        ? Number(couponInfo.discount)
                        : (Number(divisionInfo?.costPerTeam) *
                            Number(couponInfo.discount)) /
                          100
                      : 0),
                  {
                    currency: divisionInfo?.currency?.name,
                    form: 'explicit',
                  }
                )
              : '-'
          }
        />
        <EventDetails
          label='Age'
          value={age_range_type.find((el) => el.id === divisionInfo.age)?.name}
        />
        <EventDetails
          label='Gender'
          value={gender_type.find((el) => el.id === divisionInfo.gender)?.name}
        />
        <p
          css={css`
            font-size: 12px;
          `}
        >
          (Applicable taxes will be applied to all payments)
        </p>
      </div>

      <button
        disabled={disabled}
        onClick={() =>
          confirmTeamAndDivisionSelect({
            teamOfficials: teamOfficialInformation,
            teamId: selectedTeam?.uid || selectedTeam?.id,
            registrationId: divisionInfo?.registrationId,
            scheduleId: eventInfo?.id,
            couponCodeId: couponInfo?.id,
            info: {
              divisionId: divisionInfo?.divisionId,
              currency: divisionInfo?.currency,
              teamName: selectedTeam?.name,
              teamImageUrl: selectedTeam?.image?.full_path,
              costPerTeam:
                divisionInfo?.costPerTeam -
                (couponInfo.discount
                  ? couponInfo.type === 'amount'
                    ? Number(couponInfo.discount)
                    : (Number(divisionInfo?.costPerTeam) *
                        Number(couponInfo.discount)) /
                      100
                  : 0),
              deposit: divisionInfo?.deposit,
              divisionName: divisionInfo?.name,
              teamId: selectedTeam?.uid || selectedTeam?.id,
              forcePayment: divisionInfo?.depositRequired,
              singlePayment: divisionInfo?.singlePayment,
              zipCode: zipCode,
              country: selectedCountry,
            },
          })
        }
        css={css`
          cursor: ${disabled ? 'not-allowed' : 'pointer'};
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 8px;
          width: 100%;
          height: 40px;
          border-radius: 8px;
          opacity: ${disabled ? 0.5 : 1};
          background-color: ${colors.DEFAULT_FLAIR};
          color: ${colors.WHITE};
          font-size: 18px;
          font-weight: 700;
          font-family: ${isCanlan
            ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
            : 'Rift, Arial, Helvetica, sans-serif'};
          transition: all 0.2s ease-in-out;

          &:hover {
            ${!disabled &&
            `
            background-color: ${colors.DEFAULT_FLAIR_HOVER};
            `}
          }
        `}
      >
        CONFIRM MY REGISTRATION
      </button>
      <NewModal ref={newTeamOfficialModalRef} shouldCloseOnOverlayClick={false}>
        <div
          css={css`
            width: 600px;
            min-height: 300px;
            padding: 16px;
            background-color: ${isDarkMode ? '#21252A' : colors.WHITE};
            border-radius: 8px;

            .ant-select-selector {
              background-color: ${isDarkMode
                ? '#33363B'
                : '#f5f5f5'} !important;
              border: 1px solid ${colors.ATTENDANCE_GRAY} !important;
            }

            @media (max-width: 768px) {
              width: 300px;
            }
          `}
        >
          <ModalHeader
            title='Add a new team official'
            onCloseClick={() => newTeamOfficialModalRef?.current?.closeModal()}
            hideSupportButton
            titleStyles={css`
              color: ${isDarkMode ? colors.WHITE : colors.HEADER};
            `}
          />
          <div
            css={css`
              margin-top: 16px;
              color: ${isDarkMode ? colors.WHITE : colors.HEADER};

              p {
                color: ${isDarkMode ? colors.WHITE : colors.HEADER};
              }
            `}
          >
            <div>
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 8px;

                  @media (max-width: 768px) {
                    flex-direction: column;

                    > * {
                      margin-top: 0;
                    }
                  }
                `}
              >
                <Input
                  label='First Name *'
                  isDarkMode={isDarkMode}
                  onChange={(e) => {
                    setNewTeamOfficialInfo((prev) => {
                      return {
                        ...prev,
                        firstName: e.target.value,
                      }
                    })
                  }}
                />
                <Input
                  label='Last Name *'
                  isDarkMode={isDarkMode}
                  onChange={(e) => {
                    setNewTeamOfficialInfo((prev) => {
                      return {
                        ...prev,
                        lastName: e.target.value,
                      }
                    })
                  }}
                />
              </div>
              <p>Title *</p>
              <div
                css={css`
                  margin-top: 8px;
                `}
              >
                <Select
                  css={css`
                    width: 100%;
                  `}
                  onChange={(e) => {
                    const officialInfo = teamOfficialTypes.find(
                      (el) => el.value === e
                    )

                    setNewTeamOfficialInfo((prev) => {
                      return {
                        ...prev,
                        type: officialInfo.value,
                        title: officialInfo.label,
                      }
                    })
                  }}
                  placeholder='Select...'
                  options={teamOfficialTypes ?? []}
                />
              </div>
              <div
                css={css`
                  @media (max-width: 768px) {
                    margin-top: 8px;
                  }
                `}
              />
              <div>
                <Input
                  label='Email Address *'
                  inputType='email'
                  isDarkMode={isDarkMode}
                  onChange={(e) => {
                    setNewTeamOfficialInfo((prev) => {
                      return {
                        ...prev,
                        email: e.target.value,
                      }
                    })
                  }}
                />
              </div>
              <p>Phone Number</p>
              <div
                css={css`
                  display: flex;
                  align-items: center;
                  gap: 8px;
                  padding-bottom: 16px;
                  padding-top: 8px;

                  .ant-input {
                    background-color: ${isDarkMode
                      ? '#33363B'
                      : colors.WHITE} !important;
                    border: 1px solid ${colors.ATTENDANCE_GRAY} !important;
                  }
                `}
              >
                <PhoneInput
                  value={newTeamOfficialInfo.phone}
                  onChange={(e) => {
                    setNewTeamOfficialInfo((prev) => {
                      return {
                        ...prev,
                        phone: e,
                      }
                    })
                  }}
                />
              </div>
            </div>
            <button
              onClick={() => {
                setNewTeamOfficialErrorMessage('')

                if (newTeamOfficialInfo.name === '') {
                  setNewTeamOfficialErrorMessage('Please enter a Name')

                  return
                }

                if (newTeamOfficialInfo.title === '') {
                  setNewTeamOfficialErrorMessage(
                    'Please enter a Team Official Title'
                  )

                  return
                }

                if (newTeamOfficialInfo.email === '') {
                  setNewTeamOfficialErrorMessage('Please enter an Email')

                  return
                }

                const emailRegex = /\S+@\S+\.\S+/

                if (!emailRegex.test(newTeamOfficialInfo.email)) {
                  setNewTeamOfficialErrorMessage('Please enter a valid Email')

                  return
                }

                setTeamOfficialInformation((prev) => {
                  return [
                    ...prev,
                    {
                      phone: newTeamOfficialInfo.phone,
                      email: newTeamOfficialInfo.email,
                      firstName: newTeamOfficialInfo.firstName,
                      lastName: newTeamOfficialInfo.lastName,
                      title: newTeamOfficialInfo.title,
                      type: newTeamOfficialInfo.type,
                      isTreasurer: false,
                    },
                  ]
                })

                setNewTeamOfficialInfo({
                  firstName: '',
                  lastName: '',
                  email: '',
                  phone: '',
                  type: '',
                  title: '',
                })

                newTeamOfficialModalRef?.current?.closeModal()
              }}
              css={css`
                cursor: pointer;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                gap: 8px;
                width: 100%;
                height: 40px;
                border-radius: 8px;
                background-color: ${colors.DEFAULT_FLAIR};
                color: ${colors.WHITE};
                font-size: 18px;
                font-weight: 700;
                font-family: ${isCanlan
                  ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                  : 'Rift, Arial, Helvetica, sans-serif'};
                transition: all 0.2s ease-in-out;

                &:hover {
                  background-color: ${colors.DEFAULT_FLAIR_HOVER};
                }
              `}
            >
              SAVE TEAM OFFICIAL INFORMATION
            </button>
          </div>

          {newTeamOfficialErrorMessage && (
            <p
              css={css`
                color: ${colors.RED};
                margin-top: 14px;
                font-size: 16px;
                text-align: center;
                font-weight: 700;
              `}
            >
              {newTeamOfficialErrorMessage}
            </p>
          )}
        </div>
      </NewModal>
    </div>
  )
})
